import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { AmountInput, Button, DatePicker, Icon, Segmented } from '../components'
import { computeFillFromPrev } from '../lib/salary-fill'
import { useAppState } from '../app-state'
import { flattenEmployees, useEmployees } from '../lib/api/employees'
import { SALARY_DEFAULT_ORG_NAME, useDefaultOrg, useOrganizations } from '../lib/api/organizations'
import {
  flattenSalaryEntries,
  useBulkUpsertSalaries,
  usePatchSalaryEntry,
  useSalaryEntries,
} from '../lib/api/salaries'
import { fmtMoney } from '../lib/money'
import type { Currency, SalaryEntry } from '../lib/api/types'

type Row = {
  gross: number
  taxes: number
  net: number
  paymentDate: string
}

const MONTHS: { value: number; label: string }[] = [
  { value: 1, label: 'Січень' },
  { value: 2, label: 'Лютий' },
  { value: 3, label: 'Березень' },
  { value: 4, label: 'Квітень' },
  { value: 5, label: 'Травень' },
  { value: 6, label: 'Червень' },
  { value: 7, label: 'Липень' },
  { value: 8, label: 'Серпень' },
  { value: 9, label: 'Вересень' },
  { value: 10, label: 'Жовтень' },
  { value: 11, label: 'Листопад' },
  { value: 12, label: 'Грудень' },
]

function lastDayOfMonth(year: number, month: number): string {
  const day = new Date(year, month, 0).getDate()
  const mm = String(month).padStart(2, '0')
  const dd = String(day).padStart(2, '0')
  return `${year}-${mm}-${dd}`
}

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '6px 8px',
  background: 'var(--bg-1)',
  color: 'var(--fg-1)',
  fontSize: 13,
}

const numInputStyle: React.CSSProperties = {
  ...inputStyle,
  width: 120,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
}

export function AccrualTab() {
  const navigate = useNavigate()
  const { setToast } = useAppState()

  const now = new Date()
  const [periodYear, setPeriodYear] = useState<number>(now.getFullYear())
  const [periodMonth, setPeriodMonth] = useState<number>(now.getMonth() + 1)
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [currency, setCurrency] = useState<Currency>('UAH')

  const [rows, setRows] = useState<Map<string, Row>>(new Map())
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set())

  const orgs = useOrganizations()
  useDefaultOrg(orgs.data, organizationId, setOrganizationId, SALARY_DEFAULT_ORG_NAME)

  const employeesQ = useEmployees(
    organizationId ? { organizationId, isActive: true } : { isActive: true },
  )
  const entriesQ = useSalaryEntries(
    {
      organizationId,
      periodYear,
      periodMonth,
    },
    { enabled: !!organizationId },
  )

  const prevPeriod = useMemo(
    () =>
      periodMonth === 1
        ? { year: periodYear - 1, month: 12 }
        : { year: periodYear, month: periodMonth - 1 },
    [periodYear, periodMonth],
  )
  const prevEntriesQ = useSalaryEntries(
    { organizationId, periodYear: prevPeriod.year, periodMonth: prevPeriod.month },
    { enabled: !!organizationId },
  )

  const employees = useMemo(() => {
    const list = flattenEmployees(employeesQ.data)
    // Filter to org client-side too (in case backend ignores), and to active only.
    const filtered = organizationId
      ? list.filter((e) => e.organizationId === organizationId)
      : list
    return filtered
      .filter((e) => e.isActive)
      .sort((a, b) => a.fullName.localeCompare(b.fullName, 'uk'))
  }, [employeesQ.data, organizationId])

  const entries = useMemo(() => flattenSalaryEntries(entriesQ.data), [entriesQ.data])

  const entriesByEmployee = useMemo(() => {
    const map = new Map<string, SalaryEntry>()
    for (const e of entries) {
      // Filter to current currency only for display/editing.
      if (e.currency === currency) {
        map.set(e.employeeId, e)
      }
    }
    return map
  }, [entries, currency])

  const prevByEmployee = useMemo(() => {
    const map = new Map<string, SalaryEntry>()
    for (const e of flattenSalaryEntries(prevEntriesQ.data)) {
      if (e.currency === currency) map.set(e.employeeId, e)
    }
    return map
  }, [prevEntriesQ.data, currency])

  const buildRowsFromData = useCallback((): Map<string, Row> => {
    const next = new Map<string, Row>()
    const defaultPaymentDate = lastDayOfMonth(periodYear, periodMonth)
    for (const emp of employees) {
      const entry = entriesByEmployee.get(emp.id)
      next.set(
        emp.id,
        entry
          ? {
              gross: entry.gross,
              taxes: entry.taxes,
              net: entry.net,
              paymentDate: entry.paymentDate,
            }
          : { gross: 0, taxes: 0, net: 0, paymentDate: defaultPaymentDate },
      )
    }
    return next
  }, [employees, entriesByEmployee, periodYear, periodMonth])

  // Rebuild rows whenever the period/org/currency/data change.
  useEffect(() => {
    setRows(buildRowsFromData())
    setDirtyKeys(new Set())
  }, [buildRowsFromData, organizationId, currency])

  const bulkUpsert = useBulkUpsertSalaries()
  const patchEntry = usePatchSalaryEntry()
  const saving = bulkUpsert.isPending || patchEntry.isPending

  if (orgs.isLoading) {
    return <div style={{ padding: 24, color: 'var(--fg-3)' }}>Завантаження…</div>
  }

  if ((orgs.data ?? []).length === 0) {
    return (
      <div className="card" style={{ padding: 24, color: 'var(--fg-3)' }}>
        Спочатку створіть організацію
      </div>
    )
  }

  const updateRow = (employeeId: string, patch: Partial<Row>) => {
    setRows((prev) => {
      const next = new Map(prev)
      const cur = next.get(employeeId)
      if (!cur) return prev
      const merged = { ...cur, ...patch }
      // Нетто — завжди похідне: брутто − податки. Раніше всі три поля
      // редагувались незалежно, тож при введенні лише брутто нетто лишалося
      // нулем, і нарахування зберігалося з net=0 (через що «Заборгованість»
      // рахувалася хибно). Тепер нетто перераховується автоматично.
      if ('gross' in patch || 'taxes' in patch) {
        merged.net = Math.round((merged.gross - merged.taxes) * 100) / 100
      }
      next.set(employeeId, merged)
      return next
    })
    setDirtyKeys((prev) => {
      const next = new Set(prev)
      next.add(employeeId)
      return next
    })
  }

  const handleCancel = () => {
    setRows(buildRowsFromData())
    setDirtyKeys(new Set())
  }

  const handleSave = async () => {
    // bulk-upsert handles new + still-unpaid accruals; it refuses any accrual
    // with payouts ('has_payments'), so an already-paid accrual (e.g. one that
    // was under-accrued) is corrected through an explicit PATCH instead.
    const bulkItems: {
      employeeId: string
      gross: number
      taxes: number
      net: number
      currency: Currency
      paymentDate: string
    }[] = []
    const patches: { id: string; gross: number; taxes: number; net: number; paymentDate: string }[] =
      []
    for (const employeeId of dirtyKeys) {
      const row = rows.get(employeeId)
      if (!row) continue
      const entry = entriesByEmployee.get(employeeId)
      if (entry && entry.paid > 0) {
        patches.push({
          id: entry.id,
          gross: row.gross,
          taxes: row.taxes,
          net: row.net,
          paymentDate: row.paymentDate,
        })
      } else {
        bulkItems.push({
          employeeId,
          gross: row.gross,
          taxes: row.taxes,
          net: row.net,
          currency,
          paymentDate: row.paymentDate,
        })
      }
    }
    if (bulkItems.length === 0 && patches.length === 0) {
      setToast('Немає змін для збереження')
      return
    }
    try {
      await Promise.all([
        ...(bulkItems.length > 0
          ? [bulkUpsert.mutateAsync({ periodYear, periodMonth, items: bulkItems })]
          : []),
        ...patches.map(({ id, gross, taxes, net, paymentDate }) =>
          patchEntry.mutateAsync({ id, patch: { gross, taxes, net, currency, paymentDate } }),
        ),
      ])
      setDirtyKeys(new Set())
      setToast('Збережено')
    } catch {
      setToast('Помилка збереження')
    }
  }

  const fillItems = computeFillFromPrev({
    employeeIds: employees.map((e) => e.id),
    rows,
    hasCurrentEntry: (id) => !!entriesByEmployee.get(id),
    isDirty: (id) => dirtyKeys.has(id),
    prevByEmployee,
  })

  const handleFillFromPrev = () => {
    if (fillItems.length === 0) {
      setToast('Немає даних за минулий місяць')
      return
    }
    for (const it of fillItems) {
      updateRow(it.employeeId, { gross: it.gross, taxes: it.taxes })
    }
    const monthLabel = MONTHS.find((m) => m.value === prevPeriod.month)?.label ?? ''
    setToast(`Заповнено ${fillItems.length} рядків з ${monthLabel} ${prevPeriod.year}`)
  }

  const dirtyCount = dirtyKeys.size
  const dirtyGrossSum = Array.from(dirtyKeys).reduce(
    (sum, id) => sum + (rows.get(id)?.gross ?? 0),
    0,
  )
  const dirtyNetSum = Array.from(dirtyKeys).reduce(
    (sum, id) => sum + (rows.get(id)?.net ?? 0),
    0,
  )

  const isLoading = employeesQ.isLoading || entriesQ.isLoading || prevEntriesQ.isLoading
  const showOrgSelect = (orgs.data ?? []).length > 1

  return (
    <div className="card" style={{ padding: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--fg-3)' }}>
          <span>Рік</span>
          <input
            type="number"
            min={2000}
            max={2100}
            value={periodYear}
            onChange={(e) => setPeriodYear(Number(e.target.value) || now.getFullYear())}
            style={{ ...inputStyle, width: 90 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--fg-3)' }}>
          <span>Місяць</span>
          <select
            value={periodMonth}
            onChange={(e) => setPeriodMonth(Number(e.target.value))}
            style={{ ...inputStyle, width: 140 }}
          >
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
        {showOrgSelect && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--fg-3)' }}>
            <span>Організація</span>
            <select
              value={organizationId ?? ''}
              onChange={(e) => setOrganizationId(e.target.value || null)}
              style={{ ...inputStyle, width: 200 }}
            >
              {(orgs.data ?? []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--fg-3)' }}>
          <span>Валюта</span>
          <Segmented<Currency>
            options={[
              { value: 'UAH', label: 'UAH' },
              { value: 'USD', label: 'USD' },
              { value: 'EUR', label: 'EUR' },
            ]}
            value={currency}
            onChange={setCurrency}
          />
        </label>
        <div style={{ marginLeft: 'auto', alignSelf: 'flex-end' }}>
          <Button
            variant="ghost"
            size="sm"
            icon="copy"
            onClick={handleFillFromPrev}
            disabled={isLoading || saving || fillItems.length === 0}
            title={`Заповнити порожні рядки нарахуваннями за ${
              MONTHS.find((m) => m.value === prevPeriod.month)?.label ?? ''
            }`}
          >
            Заповнити з минулого місяця
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div style={{ padding: 24, color: 'var(--fg-3)' }}>Завантаження…</div>
      ) : employees.length === 0 ? (
        <div style={{ padding: 24, color: 'var(--fg-3)', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
          <div>Немає активних співробітників. Додайте їх у Налаштуваннях → Співробітники</div>
          <Button
            variant="primary"
            size="sm"
            icon="user-round"
            onClick={() => void navigate({ to: '/settings' })}
          >
            До налаштувань
          </Button>
        </div>
      ) : (
        <>
          <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-2)' }}>
                  {['ПІБ', 'Посада', 'Брутто', 'Податки', 'Нетто', 'Дата виплати', 'Статус'].map(
                    (label, idx) => (
                      <th
                        key={label}
                        style={{
                          textAlign: idx >= 2 && idx <= 4 ? 'right' : 'left',
                          padding: '8px 12px',
                          fontWeight: 500,
                          color: 'var(--fg-3)',
                        }}
                      >
                        {label}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => {
                  const entry = entriesByEmployee.get(emp.id)
                  // Has a payout (partial or full): only drives the status chip now —
                  // the amounts stay editable so an under-accrual can be corrected.
                  const paidOut = (entry?.paid ?? 0) > 0
                  const isDirty = dirtyKeys.has(emp.id)
                  const row = rows.get(emp.id) ?? {
                    gross: 0,
                    taxes: 0,
                    net: 0,
                    paymentDate: lastDayOfMonth(periodYear, periodMonth),
                  }
                  const rowStyle: React.CSSProperties = {
                    borderTop: '1px solid var(--border)',
                    background: isDirty ? 'var(--bg-2)' : undefined,
                  }
                  const cell: React.CSSProperties = { padding: '6px 12px' }
                  const numCell: React.CSSProperties = {
                    ...cell,
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                  }
                  return (
                    <tr key={emp.id} style={rowStyle}>
                      <td style={cell}>{emp.fullName}</td>
                      <td style={cell}>{emp.position ?? '—'}</td>
                      {/* Amounts stay editable even after a payout: an accrual can be
                          under-stated and need correcting. Paid edits route through
                          PATCH on save (bulk-upsert refuses paid rows). */}
                      <td style={numCell}>
                        <AmountInput
                          value={String(row.gross)}
                          onChange={(v) => updateRow(emp.id, { gross: Number(v) || 0 })}
                          decimals={2}
                          style={numInputStyle}
                        />
                      </td>
                      <td style={numCell}>
                        <AmountInput
                          value={String(row.taxes)}
                          onChange={(v) => updateRow(emp.id, { taxes: Number(v) || 0 })}
                          decimals={2}
                          style={numInputStyle}
                        />
                      </td>
                      <td style={numCell}>
                        {/* Похідне поле (брутто − податки): лише для читання, щоб
                            нетто не могло знову розійтися з брутто. */}
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={row.net}
                          readOnly
                          tabIndex={-1}
                          title="Нетто = Брутто − Податки"
                          style={{ ...numInputStyle, background: 'var(--bg-2)', color: 'var(--fg-2)', cursor: 'default' }}
                        />
                      </td>
                      <td style={cell}>
                        <DatePicker
                          value={row.paymentDate}
                          onChange={(v) => updateRow(emp.id, { paymentDate: v })}
                          style={inputStyle}
                          width={150}
                        />
                      </td>
                      <td style={cell}>
                        {paidOut ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '2px 8px',
                              borderRadius: 999,
                              fontSize: 11,
                              background: 'var(--bg-2)',
                              color: 'var(--fg-1)',
                              border: '1px solid var(--border)',
                            }}
                          >
                            <Icon name={entry?.status === 'paid' ? 'check' : 'clock'} size={12} />
                            {entry?.status === 'paid' ? 'Сплачено' : 'Частково'}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>
                            {entry ? 'Заплановано' : 'Новий'}
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginTop: 16,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>
              Змінено {dirtyCount} рядків · сума брутто{' '}
              {fmtMoney(dirtyGrossSum, currency)} · нетто {fmtMoney(dirtyNetSum, currency)}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                disabled={dirtyCount === 0 || saving}
              >
                Скасувати
              </Button>
              <Button
                variant="primary"
                size="sm"
                icon="save"
                onClick={() => void handleSave()}
                disabled={dirtyCount === 0 || saving}
              >
                {saving ? 'Збереження…' : 'Зберегти'}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
